import { useMemo, useState } from "react"
import { Link, useParams } from "react-router-dom"
import { useTranslation } from "@/i18n"
import { useThread } from "@/hooks/useThread"
import { useInteractions } from "@/hooks/useInteractions"
import { toEventId } from "@/utils/nip19"
import { NoteCard } from "@/components/NoteCard"
import { Composer } from "@/components/Composer"
import { ThreadReplies, OrphanReplies } from "@/components/Thread"
import { EmptyState, Skeleton } from "@/components/Feedback"
import { Icon } from "@/components/Icon"
import {
  ActorList,
  InteractionBar,
  ReactionSummary,
  interactionTimeMap,
  reactionEmojiMap,
  type InteractionTab,
} from "@/components/Interactions"

/**
 * Note detail: the focused note, its counters and the interaction tabs
 * (replies, reposts, reactions, quotes) plus a reply box.
 */
export function NotePage() {
  const { t } = useTranslation()
  const params = useParams()
  const noteId = toEventId(params.id ?? "")
  const { note, ancestors, replies, children, orphans, loading, refresh } = useThread(noteId)
  const interactions = useInteractions(noteId)
  const [tab, setTab] = useState<InteractionTab>("replies")

  const engagement = useMemo(
    () => ({ ...interactions.engagement, replies: replies.length }),
    [interactions.engagement, replies.length],
  )
  const reactionEmojiByActor = useMemo(
    () => reactionEmojiMap(interactions.reactions),
    [interactions.reactions],
  )
  const reactionTimeByActor = useMemo(
    () => interactionTimeMap(interactions.reactions),
    [interactions.reactions],
  )
  const repostTimeByActor = useMemo(
    () => interactionTimeMap(interactions.reposts),
    [interactions.reposts],
  )

  if (!noteId) {
    return (
      <div className="page">
        <EmptyState icon="alert" title={t("Page not found")} description="That is not a valid note id." />
      </div>
    )
  }

  if (!note && loading) {
    return (
      <div className="page">
        <Skeleton lines={4} />
      </div>
    )
  }

  if (!note) {
    return (
      <div className="page">
        <EmptyState
          icon="alert"
          title={t("Note not available")}
          description={t(
            "None of the connected relays returned this note. It may have been deleted, or it lives on relays you do not read from.",
          )}
          action={
            <Link to="/" className="btn btn-primary">
              <Icon name="home" size={16} /> {t("Back home")}
            </Link>
          }
        />
      </div>
    )
  }

  return (
    <div className="page thread-page">
      <div className="page-topbar">
        <Link to="/" className="btn btn-ghost btn-small">
          <Icon name="arrow-left" size={16} /> {t("Back home")}
        </Link>
      </div>

      {ancestors.length > 0 && (
        <section className="thread-ancestors">
          <p className="section-label">{t("In reply to")}</p>
          {ancestors.map((ancestor) => (
            <NoteCard key={ancestor.id} event={ancestor} />
          ))}
        </section>
      )}

      <section className="thread-focus">
        <p className="section-label thread-focus-label">{t("Post")}</p>
        <NoteCard
          event={note}
          engagement={engagement}
          showBookmark
          onReacted={interactions.registerReaction}
          onReposted={interactions.registerRepost}
          onReplied={refresh}
        />
        <InteractionBar
          replies={replies.length}
          reposts={interactions.reposts.length}
          reactions={interactions.summary.total}
          quotes={interactions.quotes.length}
          active={tab}
          onChange={setTab}
        />
      </section>

      <section className="thread-reply">
        <Composer
          replyTo={note}
          root={ancestors[0] ?? note}
          placeholder={t("Write a reply…")}
          compact
          onPublished={refresh}
        />
      </section>

      <section className="thread-interactions" role="tabpanel">
        {tab === "replies" && (
          <>
            {loading && replies.length === 0 && <Skeleton lines={2} />}
            {!loading && replies.length === 0 && (
              <p className="muted small">{t("No replies yet — be the first to reply.")}</p>
            )}
            <ThreadReplies
              childrenMap={children}
              parentEvent={note}
              opPubkey={note.pubkey}
              onReplied={refresh}
            />
            <OrphanReplies replies={orphans} onReplied={refresh} />
          </>
        )}

        {tab === "reposts" && (
          <ActorList
            pubkeys={interactions.repostAuthors}
            empty={t("No reposts yet.")}
            timeFor={repostTimeByActor}
          />
        )}

        {tab === "reactions" && (
          <>
            <ReactionSummary groups={interactions.summary.byEmoji} />
            <ActorList
              pubkeys={interactions.reactionAuthors}
              empty={t("No reactions yet.")}
              emojiFor={reactionEmojiByActor}
              timeFor={reactionTimeByActor}
            />
          </>
        )}

        {tab === "quotes" &&
          (interactions.quotes.length === 0 ? (
            <p className="muted small">{t("No quotes yet.")}</p>
          ) : (
            interactions.quotes.map((quote) => <NoteCard key={quote.id} event={quote} />)
          ))}
      </section>
    </div>
  )
}
